import {
	CheckCircleFilled,
	CheckCircleOutlined,
	CloseCircleOutlined,
	EditOutlined,
	InboxOutlined,
	SearchOutlined,
	SyncOutlined,
	UploadOutlined,
	UsergroupAddOutlined,
	WarningOutlined,
} from '@ant-design/icons';
import {
	Button,
	Col,
	Divider,
	Dropdown,
	Menu,
	Modal,
	Result,
	Row,
	Select,
	Space,
	Steps,
	Tooltip,
} from 'antd';
import Upload, { UploadFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import { employeeApi } from 'Api/employee';
import { Loader } from 'components/Global';
import { PermissionContext } from 'components/Global/AuthLayout';
import Ellipse from 'components/Global/Ellipse';
import SearchComponent from 'components/Global/SearchComponent';
import SimpleVirtualTable from 'components/Global/VirtualTable';
import { useCallback, useContext, useMemo, useState } from 'react';
import { DeleteActionSvg, DownloadSvg } from 'utils/svgs';
import { convertArrayToCSV, toastText } from 'utils/utils';
import BulkSalaryImport from './BulkSalaryAddModal';
import styles from './index.module.scss';
import './index.scss';

const { Step } = Steps;

const statusOptions = [
	{
		label: 'All Status',
		value: '',
	},
	{
		label: 'Active',
		value: 'ACTIVE',
	},
	{
		label: 'Suspended',
		value: 'SUSPENDED',
	},
	{
		label: 'Redundant',
		value: 'REDUNDANT',
	},
	{
		label: 'Retired',
		value: 'RETIRED',
	},
	{
		label: 'Reassigned',
		value: 'REASSIGNED',
	},
	{
		label: 'Dismissed',
		value: 'DISMISSED',
	},
	{
		label: 'Deceased',
		value: 'DECEASED',
	},
];

const steps = [
	{
		title: 'Upload CSV',
	},
	{
		title: 'Validation Results',
	},
	{
		title: 'Correct Data',
	},
	{
		title: 'Confirmation',
	},
];

const staffCodeOptions = [
	{
		label: 'All Staff Code',
		value: '',
	},
	{
		label: 'Hourly',
		value: 'HOURLY',
	},
	{
		label: 'Monthly',
		value: 'MONTHLY',
	},
];

type Props = {
	searchValue: string;
	handleSearch: (value: string) => void;
	statusFilterValue: string;
	handleStatusFilter: (value: string) => void;
	handleSearchApi: (value: string) => void;
	staffCodeFilterValue: string;
	handleStaffCodeFilter: (value: string) => void;
	fetchRecords: any;
	fetchEmployees: any;
	isLoading: any;
	totalRecords: number;
};

const EmployeeHeader = (props: Props) => {
	const {
		searchValue,
		handleSearch,
		statusFilterValue,
		handleStatusFilter,
		handleSearchApi,
		handleStaffCodeFilter,
		staffCodeFilterValue,
		fetchEmployees,
		isLoading,
		totalRecords,
	} = props;

	const permissionContext = useContext(PermissionContext);

	const isAdd =
		permissionContext.allowedPermissions.includes('Add_Employment');

	const [fileList, setFileList] = useState<UploadFile[]>([]);
	const [isUploadLoading, setIsUploadLoading] = useState(false);

	const [isModalVisible, setIsModalVisible] = useState(false);
	const [validationResults, setValidationResults] = useState([]);

	const [finalData, setFinalData] = useState([]);

	const [isErrorModal, setIsErrorModal] = useState(false);
	const [errorData, setErrorData] = useState<any>(null);

	const [currentStep, setCurrentStep] = useState(0);
	const [isImportLoading, setIsImportLoading] = useState(false);

	const [downloadLoading, setDownloadLoading] = useState(false);
	const [NrcUpdateObject, setNrcUpdateObject] = useState<any>(null);

	const next = async () => {
		if (currentStep == 1) {
			const data = validationResults.filter((item: any) => item._isValid);
			setFinalData(data);
		}
		if (currentStep == 2) {
			await importEmployees(finalData);
		} else {
			setCurrentStep((prev: any) => {
				return Math.min(prev + 1, steps.length - 1);
			});
		}
	};

	const prev = () => {
		setCurrentStep((prev) => Math.max(prev - 1, 0));
		if (currentStep === 1) {
			setFileList([]);
		}
	};

	const getClassName = (isValid: boolean) => {
		if (!isValid) {
			return 'required-color';
		} else {
			return '';
		}
	};

	const handleDelete = useCallback(
		(index: number) => {
			// Use slicing for immutable updates (more efficient than filter for large arrays)
			const updatedResults = [
				...validationResults.slice(0, index),
				...validationResults.slice(index + 1),
			];
			setValidationResults(updatedResults);

			if (currentStep === 2) {
				const updatedFinalData = [
					...finalData.slice(0, index),
					...finalData.slice(index + 1),
				];
				setFinalData(updatedFinalData);
			}

			toastText('Record deleted Successfully', 'success');
		},
		[validationResults, finalData, currentStep]
	);

	const isRecordUpdatable = (record: any) => {
		if (!NrcUpdateObject || !record['National Registration Number'])
			return false;

		const matchingEntry = NrcUpdateObject.find(
			(item: any) =>
				Object.keys(item)[0] === record['National Registration Number']
		);

		return (
			matchingEntry &&
			matchingEntry[record['National Registration Number']]?.isUpdated ===
				true
		);
	};

	const isStaffCodeChange = (record: any) => {
		if (!NrcUpdateObject || !record['National Registration Number'])
			return false;

		const matchingEntry = NrcUpdateObject.find(
			(item: any) =>
				Object.keys(item)[0] === record['National Registration Number']
		);

		return (
			matchingEntry &&
			matchingEntry[record['National Registration Number']]
				?.isStaffCodeChange === true
		);
	};

	const columns: any = useMemo(() => {
		if (!validationResults.length) return [];
		return [
			{
				title: 'Status',
				key: '_validationStatus',
				fixed: 'left',
				width: 80,
				className: 'bg-white',
				render: (_: any, record: any) =>
					record._isValid ? (
						<div
							className="text-green-500 bg-table"
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'green',
							}}
						>
							<CheckCircleOutlined />
						</div>
					) : (
						<div
							className="text-red-500 bg-table"
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'red',
							}}
						>
							<CloseCircleOutlined />
						</div>
					),
			},
			{
				title: 'Validation',
				key: '_errors',
				width: 105,
				className: 'bg-white',
				fixed: 'left',
				render: (_: any, record: any) => {
					return !record._isValid ? (
						<p
							onClick={() => {
								setIsErrorModal(true);
								setErrorData(record);
							}}
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								color: 'orange',
							}}
							className={`pointer ${getClassName(
								record._isValid
							)}`}
						>
							<WarningOutlined />
						</p>
					) : null;
				},
			},
			{
				title: 'Action',
				key: 'action',
				width: 80,
				fixed: 'left',
				render: (_: any, record: any, index: number) => {
					return (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
							}}
						>
							<Tooltip title="Delete">
								<p
									className="cursor-pointer"
									onClick={() => {
										handleDelete(index);
									}}
								>
									<DeleteActionSvg />
								</p>
							</Tooltip>
						</div>
					);
				},
			},
			...Object.keys(validationResults[0] || {})
				.filter((key) => !key.startsWith('_'))
				.map((key) => ({
					title: key,
					dataIndex: key,
					key: key,
					width: 150,
					ellipsis: true,
					className: 'bg-table bg-white',
					...((key === 'First Name' || key === 'Staff Code') && {
						fixed: 'left',
						width: 180,
					}),
					render: (text: string, record: any) => {
						const isFirstNameWithEdit =
							key === 'First Name' && isRecordUpdatable(record);

						const isStaffCodeWithWarning =
							key === 'Staff Code' && isStaffCodeChange(record);

						return (
							<div
								style={{
									minWidth: 150,
									display: 'flex',
									alignItems: 'center',
								}}
								className={`${getClassName(record._isValid)}`}
							>
								<Ellipse
									maxLength={20}
									message={text}
									isTooltip={true}
									tooltipMessage={text}
									key={text}
								/>

								{isFirstNameWithEdit && (
									<Tooltip title="This record will be updated due to matching NRC">
										<EditOutlined
											style={{
												marginLeft: 10,
												color: '#584495',
											}}
										/>
									</Tooltip>
								)}

								{isStaffCodeWithWarning && (
									<Tooltip
										title={`This employee is currently ${
											text?.toLowerCase() === 'hourly'
												? 'Monthly'
												: 'Hourly'
										}. Changing the Staff Code will switch them to ${
											text?.toLowerCase() === 'hourly'
												? 'Hourly'
												: 'Monthly'
										}, and all calculations will update accordingly.`}
									>
										<WarningOutlined
											style={{
												marginLeft: 10,
												color: 'orange',
											}}
										/>
									</Tooltip>
								)}
							</div>
						);
					},
				})),
		];
	}, [handleDelete, validationResults]);

	// Helper function to check if a record is marked for update

	const downloadEmployeeCsv = async () => {
		try {
			const response = await employeeApi.downloadEmployeeCsvTemplate();
			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'text/csv',
				})
			);

			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `employees.csv`);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (err) {
			toastText(
				'Something went wrong in downloading csv template',
				'error'
			);
		}
	};

	const downloadEmployeeCsvWithData = async () => {
		try {
			setDownloadLoading(true);
			const response =
				await employeeApi.downloadEmployeeCsvWithDataTemplate();
			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'text/csv',
				})
			);

			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `employees_data.csv`);
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (err) {
			toastText(
				'Something went wrong in downloading csv template',
				'error'
			);
		} finally {
			setDownloadLoading(false);
		}
	};

	const handleUpload = async (file: any) => {
		const reader = new FileReader();

		reader.onload = async (e: any) => {
			try {
				setIsUploadLoading(true);

				// ✅ 1. Parse CSV headers
				const csvText = e.target.result;
				const [headerLine] = csvText.split('\n');
				const uploadedColumns = headerLine
					.split(',')
					.map((col: any) => col.trim().replace(/^"(.*)"$/, '$1'));

				// ✅ 2. Define expected columns
				const jsonData = {
					'First Name*': 'Sample First Name',
					'Last Name*': 'Sample Last Name',
					'Reference Number': 'RQK001',
					'Staff Code*': 'HOURLY/MONTHLY',
					'Middle Name': 'Sample Middle Name',
					'DOB*': '12/12/2024',
					Gender: 'MALE/FEMALE',
					'Country*': 'ZAMBIA/MALAWI',
					'Marital Status': 'MARRIED/UNMARRIED',
					'Joining Date*': '11/11/2024',
					'Branch*': 'Sample Branch',
					Department: 'Sample Department',
					'Cost Center': 'Sample Cost Center',
					'Category*': 'Sample Category',
					'Job Grade': 'Sample Job Grade',
					'NHIMA Number': 'Sample NHIMA Number',
					SSN: 'Sample SSN',
					'Record Status*': 'ACTIVE/SUSPENDED',
					'Relieving Date': '26/01/2024',
					'Passport No': 'Sample Passport No',
					'National Registration Number*': '000000/00/0',
					'Currency*': 'KWACHA',
					'Employee TPIN*': '',
					'Taxation Method*': 'MONTH/TAX_EXEMPT',
					'Employment Type*': 'PERMANENT/CONTRACT',
					'Compensation Fund*': 'No',
					'Monthly Salary*': '',
					'Hourly Salary*': '',
					'Net Pay': '890.00',
					'Pay Period*': 'February (26/01/2024 - 25/02/2024)',
					'Effective Start Date*': '26/01/2024',
					'Mid Month Salary*': '5000',
					'Payment Type*': 'CASH/DIRECT_DEPOSIT',
					'Account Holder Name': '',
					'Bank Name': 'FIRST_CAPITAL_BANK/STANBIC_BANK',
					'Account Number': '',
					'Sort Code': '',
					'Beneficiary Bank Code': '',
					'Beneficiary Address 1': '',
					'Beneficiary Address 2': '',
					'Beneficiary Address 3': '',
				};

				const requiredColumns = Object.keys(jsonData);

				// ✅ 3. Validate headers
				const missingColumns = requiredColumns.filter(
					(col) => !uploadedColumns.includes(col)
				);

				if (missingColumns.length > 0) {
					let message =
						'Please Download the CSV Template to ensure correct format:\n';

					if (missingColumns.length > 0) {
						const displayMissing = missingColumns.slice(0, 3);
						message += `Missing: ${displayMissing.join(', ')}`;
						if (missingColumns.length > 3) {
							message += ` and ${missingColumns.length - 3} more`;
						}
					}

					toastText(message, 'error');
					setIsUploadLoading(false);
					setFileList([]);
					setCurrentStep(0);
					return;
				}

				// ✅ 4. Only proceed if columns match
				let formData = new FormData();
				formData.append('documentName', 'employee_csv');
				formData.append('file', file);

				const validatedData: any =
					await employeeApi.validateDataFromAPI(formData);
				const apiResponseData = validatedData.data.data;

				const mappedNrcData = apiResponseData.map(
					(item: Record<string, any>) => {
						return {
							[item['National Registration Number']]: {
								isUpdated: item.isUpdated,
								isStaffCodeChange: item.isStaffCodeChange,
							},
						};
					}
				);
				setNrcUpdateObject(mappedNrcData);

				const cleanedData = apiResponseData.map(
					(item: Record<string, any>) => {
						return Object.fromEntries(
							Object.entries(item).filter(([key]) => {
								return (
									key !== 'isUpdated' &&
									key !== '!isStaffCodeChange'
								);
							})
						);
					}
				);

				setValidationResults(cleanedData);
				setIsModalVisible(true);
				setCurrentStep(1);
			} catch (error: any) {
				let message =
					error?.response?.data?.message ||
					`Failed to process file. Please try again`;
				toastText(message, 'error');
				console.error(
					'Error during file upload and validation:',
					error
				);
				setFileList([]);
				setCurrentStep(0);
				toastText(message, 'error');
			} finally {
				setIsUploadLoading(false);
			}
		};

		reader.onerror = () => {
			toastText('File reading failed', 'error');
		};

		reader.readAsText(file);

		// Prevent default upload
		return false;
	};

	const importEmployees = async (data: any) => {
		try {
			setIsImportLoading(true);

			const csvString = convertArrayToCSV(data);

			const blob = new Blob([csvString], { type: 'text/csv' });

			const formData = new FormData();
			formData.append('file', blob, 'data.csv');

			const response = await employeeApi.importEmployeesWithFormData(
				formData
			);
			// const response = await employeeApi.importEmployees({
			// 	employees: data,
			// });
			toastText(response.data.message, 'success');
			setCurrentStep(3);
		} catch (error: any) {
			const errorMessage =
				error?.response?.data?.message ||
				'Something went wrong please while importing employees';

			toastText(errorMessage, 'error');
		} finally {
			setIsImportLoading(false);
		}
	};

	const handleModalClose = () => {
		setIsModalVisible(false);
		setErrorData(false);
		setFileList([]);
		setCurrentStep(0);
		setFinalData([]);
		setErrorData([]);
		setIsErrorModal(false);
		setIsImportLoading(false);
		setIsUploadLoading(false);
	};

	const handleImportMore = () => {
		setErrorData(false);
		setFileList([]);
		setCurrentStep(0);
		setFinalData([]);
		setErrorData([]);
		setIsErrorModal(false);
		setIsImportLoading(false);
		setIsUploadLoading(false);
	};
	const menu = (
		<>
			<Menu>
				<p style={{ padding: '5px', color: '#584495' }}>
					<span style={{ fontSize: '18px' }}>
						<UsergroupAddOutlined />
					</span>{' '}
					Employee Action
				</p>
				<Tooltip title="Employee Action">
					<Menu.Item
						style={{ backgroundColor: 'white' }}
						key="2"
						onClick={() => setIsModalVisible(true)}
						icon={<UploadOutlined />}
					>
						Upload Employee CSV File
					</Menu.Item>
					<Divider style={{ marginBottom: '3px' }} />
				</Tooltip>
				<BulkSalaryImport />
			</Menu>
		</>
	);

	return (
		<>
			<div className={styles['employee-header']}>
				<Space>
					<SearchComponent
						className={styles['employee-header-item']}
						placeHolder="Search by code or name..."
						prefixIcon={<SearchOutlined />}
						handleChange={handleSearch}
						handleChangeApi={handleSearchApi}
						value={searchValue}
						size="large"
					/>
				</Space>
				<Space>
					<Select
						className={styles['employee-header-item']}
						value={statusFilterValue}
						options={statusOptions}
						onChange={(value) => {
							handleStatusFilter(value);
						}}
						size="large"
						placeholder="Select Status"
						style={{
							maxWidth: '200px',
						}}
					/>
				</Space>
				<Space>
					<Select
						className={styles['employee-header-item']}
						value={staffCodeFilterValue}
						options={staffCodeOptions}
						onChange={(value) => {
							handleStaffCodeFilter(value);
						}}
						size="large"
						placeholder="Select Staff Code"
						style={{
							maxWidth: '200px',
						}}
					/>
				</Space>

				<div className={styles['refresh-buttons']}>
					<Tooltip title="Refresh all the Employee Details">
						<SyncOutlined
							style={{
								fontSize: '20px',
								cursor: isLoading ? 'not-allowed' : 'pointer',
								color: '#584495',
								transition: 'transform 0.3s',
								marginRight: '10px',
							}}
							onClick={!isLoading ? fetchEmployees : undefined}
						/>
					</Tooltip>
				</div>
				{totalRecords > 0 ? (
					<div
						style={{
							marginRight: '10px',
						}}
					>
						<Tooltip title="Download CSV with employee Data">
							<p
								className={styles['csv-button']}
								onClick={
									!downloadLoading
										? downloadEmployeeCsvWithData
										: undefined
								}
							>
								{downloadLoading ? (
									<>
										<Loader />
									</>
								) : (
									<DownloadSvg />
								)}
							</p>
						</Tooltip>
					</div>
				) : null}
				{isAdd && (
					<div
						style={{
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							border: '1px solid grey',
							borderRadius: '5px',
						}}
					>
						<Dropdown overlay={menu} trigger={['click']}>
							<p className={styles['menu-icon']}>
								<span
									style={{
										fontSize: '3rem',
										color: 'grey',
										marginBottom: '15px',
										padding: '5px 10px',
									}}
								>
									...
								</span>
							</p>
						</Dropdown>
					</div>
				)}
			</div>

			<Modal
				open={isModalVisible}
				onCancel={handleModalClose}
				width="1400px"
				centered
				footer={null}
				destroyOnClose
				rootClassName="uploadCsvModal"
				closable={false}
				maskClosable={false}
				// style={{
				// 	maxHeight: 'calc(100vh - 200px)',
				// 	display: 'flex',
				// 	flexDirection: 'column',
				// }}
			>
				<div>
					<Steps current={currentStep}>
						{steps.map((step, index) => (
							<Step key={index} title={step.title} />
						))}
					</Steps>

					<div
						style={{
							marginTop: '20px',
							marginBottom: '20px',
							padding: '20px',
							border: '1px solid #d9d9d9',
							borderRadius: '5px',
							flex: 1,
							overflow: 'hidden',
							display: 'flex',
							flexDirection: 'column',
							minHeight: '200px',
							justifyContent: 'center',
						}}
					>
						{currentStep === 0 && (
							<div className={styles['document-upload']}>
								{isUploadLoading ? (
									<Loader />
								) : (
									<>
										<Row>
											<Col
												span={24}
												style={{
													paddingBottom: '10px',
													paddingTop: '0px',
													display: 'flex',
													justifyContent: 'end',
												}}
											>
												<Tooltip title="Download Employee CSV Template">
													<p
														className={
															styles[
																'template-link'
															]
														}
														onClick={
															downloadEmployeeCsv
														}
													>
														<div
															style={{
																display: 'flex',
																justifyContent:
																	'center',
															}}
														>
															<DownloadSvg />
														</div>
														Download Template
													</p>
												</Tooltip>
											</Col>
										</Row>
										<Dragger
											name="file"
											multiple={false}
											accept=".csv"
											fileList={fileList}
											beforeUpload={(file) => {
												const isCSV =
													file.type === 'text/csv' ||
													file.name
														.toLowerCase()
														.endsWith('.csv');

												if (!isCSV) {
													toastText(
														'You can only upload CSV files',
														'error'
													);
													return Upload.LIST_IGNORE;
												}

												handleUpload(file);
												return false;
											}}
											onChange={(info) => {
												let newFileList = [
													...info.fileList,
												];

												// Limit to one file
												newFileList =
													newFileList.slice(-1);

												setFileList(newFileList);
											}}
										>
											<p className="ant-upload-drag-icon">
												<InboxOutlined />
											</p>
											<p className="ant-upload-text">
												Click or drag CSV file to this
												area to upload
											</p>
											<p className="ant-upload-hint">
												Support for a single CSV file
												upload with{' '}
												<span
													style={{ color: '#584495' }}
												>
													500 records
												</span>
											</p>
										</Dragger>
									</>
								)}
							</div>
						)}

						{currentStep === 1 && (
							<div className="validation-results">
								<SimpleVirtualTable
									validationResults={validationResults}
									columns={columns}
									onDelete={handleDelete}
									onErrorClick={(record) => {
										setIsErrorModal(true);
										setErrorData(record);
									}}
									NrcUpdateObject={NrcUpdateObject}
									getClassName={getClassName}
								/>
							</div>
						)}

						{currentStep === 2 && (
							<div className="validation-results">
								<SimpleVirtualTable
									validationResults={finalData}
									columns={columns.slice(0, -1)} // Remove action column
									onDelete={handleDelete}
									onErrorClick={(record) => {
										setIsErrorModal(true);
										setErrorData(record);
									}}
									NrcUpdateObject={NrcUpdateObject}
									getClassName={getClassName}
								/>
							</div>
						)}
						{currentStep === 3 && (
							<Result
								status="success"
								title={`The process of importing ${
									finalData.length > 1
										? 'employees'
										: 'an employee'
								} has started.`}
								subTitle={`Processing ${
									finalData.length > 1
										? 'employees'
										: 'employee'
								} record. Only ${
									finalData.length
								} validated and imported. This may take a moment, Please wait....`}
								icon={
									<CheckCircleFilled
										style={{ color: '#584495' }}
									/>
								}
								extra={[
									<Button
										type="primary"
										key="console"
										onClick={handleImportMore}
									>
										Import More
									</Button>,
									<Button
										key="buy"
										onClick={handleModalClose}
									>
										Close
									</Button>,
								]}
							/>
						)}
					</div>

					{currentStep !== 3 && (
						<div
							style={{
								marginTop: '20px',
								display: 'flex',
								justifyContent: 'end',
								gap: '15px',
							}}
						>
							<Button onClick={handleModalClose}>Cancel</Button>
							{currentStep !== 0 && (
								<Button
									disabled={
										currentStep === 0 ||
										isImportLoading ||
										isUploadLoading
									}
									onClick={prev}
									type="primary"
								>
									Previous
								</Button>
							)}
							{fileList.length > 0 && (
								<Button
									disabled={
										currentStep === steps.length - 1 ||
										isImportLoading ||
										isUploadLoading ||
										(currentStep === 1 &&
											validationResults.length === 0) ||
										(currentStep === 2 &&
											finalData.length === 0)
									}
									type="primary"
									onClick={next}
								>
									{currentStep === 2
										? 'Import Employees'
										: 'Next'}
								</Button>
							)}
						</div>
					)}
				</div>
			</Modal>

			{errorData && isErrorModal && (
				<Modal
					open={isErrorModal}
					onCancel={() => {
						setErrorData(null);
						setIsErrorModal(false);
					}}
					destroyOnClose
					rootClassName="uploadCsvModal"
					width="90%"
					footer={null}
				>
					<ul
						className="error-list"
						style={{
							listStyleType: 'none',
							padding: '10px',
							borderRadius: '5px',
							border: '1px solid #d4d4d4',
							color: '#721c24',
							fontFamily: 'Arial, sans-serif',
							maxHeight: '450px',
							overflowY: 'auto',
						}}
					>
						{Object.entries(errorData._validationErrors).map(
							([field, error]: any) => (
								<li
									key={field}
									style={{
										padding: '8px 12px',
										borderBottom: '1px solid #d4d4d4',
									}}
								>
									<span
										style={{
											fontWeight: 'bold',
											color: '#b02a2a',
										}}
									>
										{field}
									</span>{' '}
									:{' '}
									<span
										style={{
											fontStyle: 'italic',
											color: 'black',
										}}
									>
										{error}
									</span>
								</li>
							)
						)}
					</ul>
				</Modal>
			)}
		</>
	);
};

export default EmployeeHeader;
